import { useLocation, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Home } from "lucide-react";

const routeLabels: Record<string, string> = {
  pricing: "Pricing",
  integrations: "Integrations",
  blog: "Blog",
  contact: "Contact Us",
  team: "Our Team",
  careers: "Careers",
  features: "Features",
  solutions: "Solutions",
  competitors: "Competitors",
  support: "Support",
  roadmap: "Product Roadmap",
  "free-trial": "Free Trial",
  "about-us": "About Us",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  sitemap: "Sitemap",
  ecommerce: "E-commerce Solutions",
  manufacturing: "Manufacturing Solutions",
  wholesale: "Wholesale Solutions",
  fashion: "Fashion & Apparel Solutions",
  shopify: "Shopify Integration",
  "google-shopping": "Google Shopping Integration",
  akeneo: "Ommni vs Akeneo",
  pimberly: "Ommni vs Pimberly",
  "sales-layer": "Ommni vs Sales Layer",
  salsify: "Ommni vs Salsify",
  "ultra-commerce": "Ommni vs Ultra Commerce",
  "ommni-flow": "OmmniFlow",
  pim: "Product Information Management",
  dam: "Digital Asset Management",
  dom: "Distributed Order Management",
  offers: "Offers & Pricing",
  inventory: "Inventory Management",
  admin: "Admin",
  auth: "Authentication",
};

export default function Breadcrumbs() {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  if (pathSegments.length === 0) return null;

  // Build breadcrumb items for structured data
  const breadcrumbItems = [
    {
      position: 1,
      name: "Home",
      item: "https://ommni.io"
    },
    ...pathSegments.map((segment, index) => {
      const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
      const label = routeLabels[segment] || segment.replace(/-/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      return {
        position: index + 2,
        name: label,
        item: `https://ommni.io${path}`
      };
    })
  ];

  // Generate JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map(item => ({
      "@type": "ListItem",
      "position": item.position,
      "name": item.name,
      "item": item.item
    }))
  };

  return (
    <>
      {/* JSON-LD Structured Data for Breadcrumbs */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="flex items-center gap-1">
                    <Home className="h-4 w-4" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>

              {pathSegments.map((segment, index) => {
                const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
                const label = routeLabels[segment] || segment.replace(/-/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                const isLast = index === pathSegments.length - 1;

                return (
                  <div key={path} className="flex items-center gap-2">
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage>{label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={path}>{label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </div>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
    </>
  );
}
